/**
 * v2 CallResponse → QueueModel Mapper
 *
 * Converts v2 backend call response structures (used in both API responses
 * and Socket.IO events) to the frontend QueueModel domain type.
 */

import type { V2CallResponse } from '@/shared/services/socketio/types';
import type { QueueModel, CallStatus } from '../../domain/types';

/**
 * Map a v2 CallResponse to a QueueModel.
 * Used by:
 *   - queueDatasource (POST /app/queue/paginated response)
 *   - Socket.IO created event handlers
 */
export function v2CallResponseToQueueModel(data: V2CallResponse): QueueModel {
  const callee = data.callees?.[0];

  return {
    id: data.id,
    name: data.name ?? callee?.name ?? '',
    role: data.role ?? callee?.role ?? '',
    relationship: data.relationship ?? '',
    time_start: data.time_start,
    time_end: data.time_end,
    status: data.status as CallStatus,
    order: data.sequence_number ?? 0,
    callee: callee?.name ?? '',
    caller: {
      id: data.caller?.id,
      full_name: data.caller?.name,
      photo_url: data.caller?.photo_url,
    },
    callee_details: callee
      ? {
          name: callee.name,
          nric: callee.nric,
          role: callee.role,
        }
      : undefined,
    forward_from: data.forward_from
      ? { id: data.forward_from.id, name: data.forward_from.name }
      : undefined,
    has_transcript: data.has_transcript,
    is_fallback: data.is_fallback,
    has_call_detail: data.has_call_detail,
    // Extract from case_info sub-object
    case_id: data.case_info?.case_id,
    case_external_id: data.case_info?.case_external_id,
    loan_id: data.case_info?.loan_id,
    tags: data.case_info?.tags ?? [],
    tier: data.case_info?.tier ?? '',
    amount_due: data.case_info?.ammount_due,
    case_status: data.case_info?.case_status,
    last_payment_date: data.case_info?.last_payment_date,
    next_case_id: data.case_info?.next_case_id,
    dpd: data.case_info?.dpd,
    is_incoming_call: data.type === 'inbound',
  };
}
